import {
  syncOrchestrator,
  buildSnapshotFromLocalStorage,
} from "../api/services/syncOrchestrator";
import { authSyncAPI } from "../api/services/authSyncAPI";

function getAuthSafe(getAuth) {
  try {
    return getAuth?.() || {};
  } catch {
    return {};
  }
}
function onLoginRoute() {
  return (
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/login")
  );
}
function hasCreds(auth) {
  return !!auth?.authToken && !!auth?.systemName; // syncToken is optional at first boot
}

export function wireGlobalSyncHooks(getAuth) {
  if (typeof window === "undefined") return () => {};

  // ---- 0) init orchestrator once (no network here) ----
  syncOrchestrator.init?.({
    getAuth,
    on401: () => {
      try {
        syncOrchestrator.stop();
        syncOrchestrator.disabled = true; // if you’re using the circuit-breaker
      } catch {}
      window.location.replace("/login");
    },
  });

  // ---- 1) start only when authed AND not on /login ----
  const tryStart = (reason = "bootstrap") => {
    if (onLoginRoute()) return;
    const auth = getAuthSafe(getAuth);
    if (!hasCreds(auth)) return;
    // only start once
    if (!syncOrchestrator.interval && !syncOrchestrator.disabled) {
      syncOrchestrator.start?.();
    }
  };

  // initial attempt (will no-op on /login or when logged out)
  tryStart("initial");

  // ---- 2) resume/force only when authed (visibility) ----
  const onVisibility = () => {
    if (onLoginRoute()) return;
    const auth = getAuthSafe(getAuth);
    if (!hasCreds(auth) || syncOrchestrator.disabled) return;

    if (document.visibilityState === "hidden") {
      syncOrchestrator.forceSyncNow("visibility-hidden");
    } else if (document.visibilityState === "visible") {
      // make sure orchestrator is started after coming back
      tryStart("visibility-visible");
      syncOrchestrator.forceSyncNow("visibility-visible");
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  // ---- 3) last-chance keepalive on close/navigation (only when authed) ----
  const onPageHide = () => {
    if (onLoginRoute()) return;
    const auth = getAuthSafe(getAuth);
    if (!hasCreds(auth) || !auth?.syncToken) return;

    try {
      const snapshot = buildSnapshotFromLocalStorage();
      authSyncAPI.syncSnapshotKeepAlive({
        snapshot,
        authToken: auth.authToken,
        systemName: auth.systemName,
        syncToken: auth.syncToken,
      });
    } catch {}
  };
  window.addEventListener("pagehide", onPageHide);

  // ---- 4) OPTIONAL: auto-start after login ----
  // (A) other tabs or after a hard refresh: when authToken hits LS, try start
  const onStorage = (e) => {
    if (e.key === "authToken" && e.newValue) {
      // give router a tick to leave /login
      setTimeout(() => tryStart("storage-auth"), 0);
    }
  };
  window.addEventListener("storage", onStorage);

  // (B) same-tab: dispatch this after successful login
  //   window.dispatchEvent(new Event("WTUFO_AUTH_READY"));
  const onAuthReady = () => setTimeout(() => tryStart("auth-ready"), 0);
  window.addEventListener("WTUFO_AUTH_READY", onAuthReady);

  // disposer (tests/HMR)
  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("WTUFO_AUTH_READY", onAuthReady);
  };
}
